import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Building2, ExternalLink, ChevronRight } from 'lucide-react';
import { experienceData, educationData, certificationsData } from '../data/mock';

const ExperienceSection = () => {
  return (
    <section id="about" className="py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00ff88]/[0.02] to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-[#00ff88] text-sm font-medium uppercase tracking-wider mb-4 block">
            Career Journey
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Work <span className="gradient-text">Experience</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Experience Timeline */}
          <div className="lg:col-span-2 space-y-6">
            {experienceData.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bento-card p-6 md:p-8 relative overflow-hidden group"
              >
                {/* Accent Line */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00ff88] via-[#00ccff] to-[#00ff88] opacity-50 group-hover:opacity-100 transition-opacity" />
                
                <div className="pl-4">
                  {/* Header */}
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-4">
                      {exp.logo ? (
                        <img
                          src={exp.logo}
                          alt={exp.company}
                          className="w-12 h-12 object-contain bg-white rounded-xl p-2"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-[#00ff88]/10 rounded-xl flex items-center justify-center">
                          <Building2 size={20} className="text-[#00ff88]" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-display text-lg font-semibold text-white">
                          {exp.company}
                        </h3>
                        <p className="text-[#00ff88] text-sm font-medium">{exp.role}</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-white/5 rounded-full text-white/50 text-xs">
                      {exp.type}
                    </span>
                  </div>

                  {/* Meta Info */}
                  <div className="flex flex-wrap gap-4 mb-4 text-white/40 text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{exp.duration}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin size={14} />
                      <span>{exp.location}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-white/60 text-sm mb-4">{exp.description}</p>

                  {/* Highlights */}
                  <ul className="space-y-2">
                    {exp.highlights.slice(0, 3).map((highlight, hIndex) => (
                      <li key={hIndex} className="flex items-start gap-2 text-white/50 text-sm">
                        <ChevronRight size={14} className="text-[#00ff88] flex-shrink-0 mt-1" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Sidebar - Education & Certs */}
          <div className="space-y-6">
            {/* Education Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bento-card p-6"
            >
              <span className="text-[#00ff88] text-xs font-medium uppercase tracking-wider mb-4 block">
                Education
              </span>
              
              <div className="flex items-start gap-4 mb-4">
                <img
                  src={educationData.logo}
                  alt={educationData.institution}
                  className="w-14 h-14 object-contain"
                />
                <div>
                  <h3 className="font-display font-semibold text-white text-sm">
                    {educationData.degree}
                  </h3>
                  <p className="text-[#00ff88] text-xs">{educationData.major}</p>
                  <p className="text-white/40 text-xs mt-1">Minor: {educationData.minor}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-white/5">
                <p className="text-white/40 text-xs">{educationData.institution}</p>
                <p className="text-white/40 text-xs">Graduated: {educationData.graduationDate}</p>
                <p className="text-[#00ff88] text-xs mt-1">GPA: {educationData.gpa}</p>
              </div>
            </motion.div>

            {/* Certifications Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bento-card p-6"
            >
              <span className="text-[#00ff88] text-xs font-medium uppercase tracking-wider mb-4 block">
                Certifications
              </span>
              
              <div className="space-y-3">
                {certificationsData.map((cert) => (
                  <div
                    key={cert.id}
                    className="p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                  >
                    <p className="text-white text-sm font-medium">{cert.name}</p>
                    <p className="text-white/40 text-xs mt-1">
                      {cert.issuer} • {cert.year}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* CTA Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bento-card p-6 border-[#00ff88]/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="w-3 h-3 bg-[#00ff88] rounded-full animate-pulse" />
                <span className="text-[#00ff88] text-sm font-medium">
                  Available for opportunities
                </span>
              </div>
              <p className="text-white/50 text-sm mb-4">
                Ready to discuss how I can help secure your digital infrastructure.
              </p>
              <a href="#contact" className="btn-primary w-full justify-center text-sm">
                Let's Connect
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
